import * as Yup from "yup";

import { ZERO_VALUE, PERCENTAGE_BASE } from "../../constants";

export const createListingSchema = Yup.object().shape({
  listingType: Yup.string().required("Listing Type is required"),
  availability: Yup.number().required("Availability is required"),
  openHouse: Yup.boolean(),
  startDate: Yup.number()
    .nullable()
    .when("openHouse", {
      is: true,
      then: (schema) => schema.required(""),
    }),
  endDate: Yup.number()
    .nullable()
    .when("openHouse", {
      is: true,
      then: (schema) =>
        schema
          .required("")
          .test(
            "is-after-start",
            "End Date must be after Start Date",
            function (value) {
              return (
                !this.parent.startDate ||
                (value && value > this.parent.startDate)
              );
            }
          ),
    }),
  area: Yup.number()
    .required("Area is required")
    .min(1, "Area cannot be negative or zero"),
  areaMeasurementType: Yup.number().required(
    "Area Measurement Type is required"
  ),
  price: Yup.number()
    .required("Price is required")
    .min(1, "Price cannot be negative or zero"),
  depositAmount: Yup.number()
    .required("Deposit Amount is required")
    .min(1, "Deposite Amount cannot be negative or zero"),
  depositPercentage: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === "" ||
      originalValue === null ||
      originalValue === undefined
        ? null
        : value
    )
    .min(ZERO_VALUE, "Deposit percentage cannot be negative")
    .max(PERCENTAGE_BASE, "Deposit percentage cannot exceed 100"),
  currencyId: Yup.number().required("Currency is required"),
  title: Yup.string()
    .trim()
    .required("Title is required")
    .max(200, "Title must not exceed 200 characters"),
  description: Yup.string()
    .trim()
    .required("Description is required")
    .max(5000, "Description must not exceed 5000 characters"),
  city: Yup.string().trim().required("City is required"),
  addressLine1: Yup.string().trim().required("Address Line 1 is required"),
  addressLine2: Yup.string()
    .trim()
    .nullable()
    .max(200, "Address line 2 must not exceed 200 characters"),
  state: Yup.string().trim().required("State is required"),
  countryId: Yup.number().required("Country is required"),
  propertyTypeIds: Yup.array()
    .of(Yup.number())
    .min(1, "At least one property type is required")
    .max(3, "You can select maximim 3 property types")
    .required("Property Type is required"),
  viewTypeIds: Yup.array()
    .of(Yup.number())
    .min(1, "At least one view type is required")
    .max(3, "You can select maximim 3 view types")
    .required("View Type is required"),
  amenity: Yup.object().shape({
    bedrooms: Yup.string().required("Bedrooms is required"),
    bathrooms: Yup.string().required("Bathrooms is required"),
  }),
  images: Yup.array()
    .of(
      Yup.lazy((value) => {
        // Check if the value is a File object or an object with specific properties
        if (
          value &&
          typeof value === "object" &&
          "id" in value &&
          "url" in value &&
          "type" in value
        ) {
          // Validate object structure
          return Yup.object().shape({
            id: Yup.number(),
            url: Yup.string(),
            type: Yup.number(),
          });
        } else {
          // Validate File object
          return Yup.mixed()
            .required("An image file is required")
            .test(
              "fileType",
              "Only PNG, JPG, and JPEG files are allowed",
              (file) => {
                if (!file) return true; // Allow empty files
                const validTypes = ["image/png", "image/jpeg", "image/jpg"];
                const isValidType = validTypes.includes(file.type);
                const fileName = file.name?.toLowerCase();
                const isJfif = fileName?.endsWith(".jfif");

                return isValidType && !isJfif;
              }
            );
        }
      })
    )
    .min(1, "At least one image is required") // Minimum of 1 image
    .max(10, "You can upload maximum 10 images") // Maximum of 10 images
    .required("At least one image is required"), // Ensures the field is present
});

const emptyToUndefined = (value, originalValue) => {
  if (
    originalValue === "" ||
    originalValue === null ||
    originalValue === undefined ||
    (typeof originalValue === "number" && Number.isNaN(originalValue))
  ) {
    return undefined;
  }
  return value;
};

const optionalNumber = () =>
  Yup.number().transform(emptyToUndefined).nullable().notRequired();

const optionalString = () =>
  Yup.string()
    .transform((value, originalValue) =>
      originalValue === null || originalValue === undefined ? undefined : value
    )
    .nullable()
    .notRequired();

const draftAmenitySchema = Yup.object()
  .shape({
    bedrooms: optionalNumber().default(ZERO_VALUE),
    bathrooms: optionalNumber().default(ZERO_VALUE),
    kitchens: optionalNumber(),
    otherRooms: optionalNumber(),
    staffQuarters: optionalNumber(),
    storeRooms: optionalNumber(),
    floors: optionalNumber(),
    builtInYear: optionalNumber(),
    parkingSpace: optionalNumber(),
    flooringType: optionalNumber(),
    electricityBackup: optionalNumber(),
    propertyView: optionalNumber(),
    featureIds: Yup.array().of(Yup.number()).nullable().notRequired(),
  })
  .nullable()
  .notRequired();

const draftNearbyLocationSchema = Yup.object()
  .shape({
    id: optionalNumber(),
    type: optionalNumber(),
    name: optionalString(),
    distance: optionalNumber(),
    distanceKm: optionalNumber(),
    distanceUnit: optionalNumber(),
  })
  .noUnknown(true);

export const createListingDraftSchema = Yup.object().shape({
  listingType: optionalNumber(),
  availability: optionalNumber(),
  openHouse: Yup.boolean().notRequired(),
  startDate: optionalNumber(),
  endDate: optionalNumber(),
  area: optionalNumber(),
  areaMeasurementType: optionalNumber(),
  price: optionalNumber(),
  depositAmount: optionalNumber(),
  depositPercentage: Yup.mixed().strip(),
  currencyId: optionalNumber(),
  title: optionalString(),
  description: optionalString(),
  city: optionalString(),
  addressLine1: optionalString(),
  addressLine2: optionalString(),
  state: optionalString(),
  countryId: optionalNumber(),
  latitude: optionalNumber(),
  longitude: optionalNumber(),
  propertyTypeIds: Yup.array().of(Yup.number()).nullable().notRequired(),
  viewTypeIds: Yup.array().of(Yup.number()).nullable().notRequired(),
  amenity: draftAmenitySchema,
  nearbyLocations: Yup.array()
    .of(draftNearbyLocationSchema)
    .nullable()
    .notRequired(),
  // Preserve File objects and existing { id, url, type } image entries as-is.
  images: Yup.array().of(Yup.mixed()).nullable().notRequired(),
});

const omitEmptyValues = (value) => {
  if (Array.isArray(value)) {
    return value.map(omitEmptyValues);
  }
  if (value && typeof value === "object" && !(value instanceof File)) {
    return Object.entries(value).reduce((acc, [key, entry]) => {
      if (entry === undefined || entry === null) {
        return acc;
      }
      acc[key] = omitEmptyValues(entry);
      return acc;
    }, {});
  }
  return value;
};

export const sanitizeDraftPayload = (data) => {
  const castPayload = createListingDraftSchema.cast(data, { assert: false });
  return omitEmptyValues(castPayload);
};
